import { useEffect, useMemo, useState } from 'react';
import { Layout } from '../components/Layout';
import { Card, Badge, Select, Input } from '../components/ui';
import { calendarDate } from '../lib/calendarDate';
import { api, ApiError } from '../lib/api';
import { currentUser } from '../lib/auth';
import type { ApiResponse } from '../lib/types';

interface Invoice {
  invoice_id: string; client_id?: number; client_name?: string;
  period_month?: number; period_year?: number;
  pageviews?: number; clicks?: number; cost?: number; invoice_value?: number;
  status?: string; due_date?: string; view_hash?: string;
}
interface ClientOpt { id: number; name: string }

const now = new Date();
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const n = (v: unknown) => Number(v) || 0;
const usd = (v: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v);

export default function Billing() {
  const isAdmin = !!currentUser()?.isAdmin;
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [clients, setClients] = useState<ClientOpt[]>([]);
  const [clientId, setClientId] = useState('');
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isAdmin) api.get<ApiResponse<ClientOpt[]>>('/clients/list').then((r) => setClients(r.data || [])).catch(() => {});
  }, [isAdmin]);

  useEffect(() => {
    setLoading(true); setError(null);
    // admin vê todas as invoices do período (/finance/invoices); cliente vê as suas (/user/invoices)
    const url = isAdmin ? `/finance/invoices?month=${month}&year=${year}` : '/user/invoices';
    api.get<ApiResponse<Invoice[]>>(url)
      .then((r) => setInvoices(r.data || []))
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Failed to load'))
      .finally(() => setLoading(false));
  }, [isAdmin, month, year]);

  const filtered = useMemo(() => {
    let out = invoices;
    if (isAdmin && clientId) {
      const name = clients.find((c) => String(c.id) === clientId)?.name;
      out = out.filter((i) => String(i.client_id ?? '') === clientId || (name && i.client_name === name));
    }
    const q = search.trim().toLowerCase();
    if (q) out = out.filter((i) => `${i.invoice_id} ${i.client_name || ''}`.toLowerCase().includes(q));
    return out;
  }, [invoices, isAdmin, clientId, clients, search]);

  const total = filtered.reduce((a, i) => a + n(i.invoice_value), 0);
  const paid = filtered.filter((i) => i.status === 'paid').reduce((a, i) => a + n(i.invoice_value), 0);

  return (
    <Layout
      title="Billing"
      actions={isAdmin ? (
        <div className="flex gap-2">
          <Select value={clientId} onChange={(e) => setClientId(e.target.value)} className="w-48">
            <option value="">All clients</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </Select>
          <Select value={month} onChange={(e) => setMonth(Number(e.target.value))} className="w-24">{MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}</Select>
          <Select value={year} onChange={(e) => setYear(Number(e.target.value))} className="w-24">{[year, year - 1, year - 2].map((y) => <option key={y} value={y}>{y}</option>)}</Select>
        </div>
      ) : undefined}
    >
      {error && <div className="mb-4 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-sm px-4 py-3">{error}</div>}

      {isAdmin && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
            <Card className="p-4"><div className="text-2xl font-bold text-ink-900">{filtered.length}</div><div className="text-sm text-slate-500">Invoices</div></Card>
            <Card className="p-4"><div className="text-2xl font-bold text-ink-900">{usd(total)}</div><div className="text-sm text-slate-500">Total invoiced</div></Card>
            <Card className="p-4"><div className="text-2xl font-bold text-emerald-600">{usd(paid)}</div><div className="text-sm text-slate-500">Total paid</div></Card>
          </div>
          <div className="mb-4">
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by invoice or client…" />
          </div>
        </>
      )}

      <Card className="overflow-hidden">
        <div className="px-4 py-3 border-b border-border text-sm font-semibold text-ink-900">
          {isAdmin ? `Invoices from all clients (${MONTHS[month - 1]}/${year})` : 'My Invoices'}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase text-slate-500 bg-surface-muted">
                <th className="px-4 py-2.5 font-medium">Invoice</th>
                {isAdmin && <th className="px-4 py-2.5 font-medium">Client</th>}
                <th className="px-4 py-2.5 font-medium">Period</th>
                {isAdmin && <th className="px-4 py-2.5 font-medium text-right">Clicks</th>}
                {isAdmin && <th className="px-4 py-2.5 font-medium text-right">Cost</th>}
                <th className="px-4 py-2.5 font-medium text-right">Value</th>
                <th className="px-4 py-2.5 font-medium">Status</th>
                <th className="px-4 py-2.5 font-medium">Due Date</th>
                <th className="px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading && <tr><td colSpan={isAdmin ? 9 : 6} className="px-4 py-6 text-slate-400">Loading…</td></tr>}
              {!loading && filtered.map((iv) => (
                <tr key={iv.invoice_id} className="hover:bg-surface-muted/60">
                  <td className="px-4 py-2.5 font-mono text-xs text-slate-600">{iv.invoice_id}</td>
                  {isAdmin && <td className="px-4 py-2.5 text-slate-700">{iv.client_name || '—'}</td>}
                  <td className="px-4 py-2.5 text-slate-600">{iv.period_month}/{iv.period_year}</td>
                  {isAdmin && <td className="px-4 py-2.5 text-right text-slate-600">{n(iv.clicks).toLocaleString()}</td>}
                  {isAdmin && <td className="px-4 py-2.5 text-right text-slate-600">{usd(n(iv.cost))}</td>}
                  <td className="px-4 py-2.5 text-right font-medium text-slate-700">{usd(n(iv.invoice_value))}</td>
                  <td className="px-4 py-2.5"><Badge tone={iv.status === 'paid' ? 'green' : iv.status === 'overdue' ? 'red' : 'amber'}>{iv.status || 'pending'}</Badge></td>
                  <td className="px-4 py-2.5 text-slate-500">{iv.due_date ? calendarDate(iv.due_date) : '—'}</td>
                  <td className="px-4 py-2.5">{iv.view_hash && <a className="text-brand-600 hover:underline text-xs" href={`/_api/invoice/view/${iv.view_hash}`} target="_blank" rel="noreferrer">View / Pay</a>}</td>
                </tr>
              ))}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={isAdmin ? 9 : 6} className="px-4 py-10 text-center text-slate-400">
                  {isAdmin ? 'No invoices in this period.' : 'No invoices.'}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </Layout>
  );
}
